/**
 * The page a QR code on a printed invoice or offer opens: the whole document,
 * with no account and no password.
 *
 * It sits ABOVE the session gate in `main.tsx`, which is the point of it. The
 * person holding the paper is the person meant to read it, and asking them to
 * create an account first is how a payment link goes unused. The token in the
 * address is the whole credential, and the server treats a missing, revoked or
 * still-draft document as one and the same 404 - so this screen has exactly two
 * states, the document or "this link is not valid".
 *
 * The page carries a way IN as well, and deliberately near the bottom rather
 * than as a wall at the top: everything about THIS document is readable without
 * signing in, and the portal is offered for what the link cannot show - the
 * client's other invoices, their statement, their projects.
 *
 * LAYOUT. Two columns from `lg` up. The left one is the document itself and
 * nothing else, in the order the paper prints it; the right rail is everything
 * the reader can DO about it - what is owed, how to pay it, whether to accept
 * it, what to ask about it. The split is what lets the page be wide: a single
 * column of full-width cards has to stay narrow to keep the line table
 * readable, and then a 320px payment code sits alone on a 1200px screen. Under
 * `lg` the two stack, document first, which is also the order the page reads on
 * a phone held over a printed invoice.
 *
 * `noindex` is set on mount. A public address is not a published one, and a
 * search engine that finds one forwarded link must not go on to list it.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Download, FileText, Loader2, LogIn } from "lucide-react";
import { Button, Card, CardContent } from "@wa/ui";
import { Logo } from "../../components/Logo";
import { localeFromUrl, useI18n, useT } from "../../i18n/I18nProvider";
import {
  publicDoc, publicPdfHref,
  type PublicDocComment, type PublicDocResponse,
} from "../../lib/publicDoc";
import { DocComments } from "./DocComments";
import { DocDecision } from "./DocDecision";
import { DocLines } from "./DocLines";
import { DocParties } from "./DocParties";
import { DocPayment } from "./DocPayment";
import { DocSummary } from "./DocSummary";

function useNoIndex(): void {
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    return () => { tag.remove(); };
  }, []);
}

/**
 * The bar over every state of this page, brand mark and all.
 *
 * The mark is `Logo`, so the host decides it the way it decides it everywhere
 * else in this app (see lib/brand.ts): the operator's wordmark on the portal
 * host the QR codes actually point at, Husly's own anywhere else. A link that
 * opens on a page with no sender on it reads like a page that could be anyone's,
 * which is the last thing a payment request should look like.
 *
 * Sticky, because the one action that belongs beside the mark is "download the
 * PDF" and a line table can be two screens long.
 */
function DocShell({ action, children }: { action?: ReactNode; children: ReactNode }) {
  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 py-3">
          <Logo variant="full" className="h-6 w-auto text-foreground md:h-7" />
          {action}
        </div>
      </header>
      {children}
    </div>
  );
}

export function PublicDocPage({ token }: { token: string }) {
  const t = useT();
  const { applyLocale } = useI18n();
  const [state, setState] = useState<PublicDocResponse | null | "loading" | "missing">("loading");
  useNoIndex();

  useEffect(() => {
    let live = true;
    publicDoc.read(token)
      .then((res) => { if (live) setState(res); })
      .catch(() => { if (live) setState("missing"); });
    return () => { live = false; };
  }, [token]);

  // The page follows the DOCUMENT's language, so a client sent a German invoice
  // reads a German page - the same words the PDF behind the link carries.
  //
  // Two things it deliberately does not do. It does not remember the choice
  // (`applyLocale`, not `setLocale`): the reader did not ask for this language,
  // the document did, and a portal client who opens one foreign invoice must
  // not find their whole interface switched afterwards. And an explicit `?lang=`
  // in the address WINS, because that is a reader asking - somebody who cannot
  // read the language the document was written in has one way to say so, and it
  // has to keep working.
  const docLocale = typeof state === "object" && state ? state.doc.locale : undefined;
  useEffect(() => {
    if (!docLocale || localeFromUrl()) return;
    applyLocale(docLocale);
  }, [docLocale, applyLocale]);

  if (state === "loading") {
    return (
      <DocShell>
        <div className="flex min-h-[60dvh] items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </DocShell>
    );
  }

  if (state === "missing" || !state) {
    return (
      <DocShell>
        <div className="flex min-h-[60dvh] items-center justify-center px-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 text-center">
              <FileText className="mx-auto size-8 text-muted-foreground" />
              <h1 className="mt-3 text-lg font-semibold text-foreground">{t("publicDoc.missing.title")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{t("publicDoc.missing.hint")}</p>
            </CardContent>
          </Card>
        </div>
      </DocShell>
    );
  }

  const doc = state.doc;
  const onAnswered = (next: PublicDocResponse) => setState(next);
  const onComment = (c: PublicDocComment) =>
    setState({ ...state, doc: { ...doc, comments: [...doc.comments, c] } });

  return (
    <DocShell
      action={
        <Button asChild variant="outline" size="sm">
          <a href={publicPdfHref(token)} target="_blank" rel="noreferrer">
            <Download className="size-4" />
            {t("publicDoc.downloadPdf")}
          </a>
        </Button>
      }
    >
      <main className="mx-auto w-full max-w-7xl px-4 py-6 lg:py-8">
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_24rem]">
          {/* The document, in the order the paper prints it. */}
          <div className="min-w-0 space-y-5">
            <DocParties doc={doc} />
            <DocLines doc={doc} />
          </div>

          {/* Everything the reader can do about it. */}
          <aside className="min-w-0 space-y-5">
            <DocSummary doc={doc} />

            {/* `payTransfer !== false` and not `=== true`: the field is absent
                on a bundle cached before the switch existed, and absent has to
                keep meaning what it always did. See `lib/publicDoc.ts`. */}
            {doc.type === "invoice" && doc.dueCents > 0 && doc.payTransfer !== false ? (
              <DocPayment doc={doc} />
            ) : null}

            <DocDecision token={token} doc={doc} onAnswered={onAnswered} />

            <DocComments token={token} comments={doc.comments} onAdded={onComment} />

            {/* The way in, for everything this one link cannot show. */}
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm font-medium text-foreground">{t("publicDoc.login.title")}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t("publicDoc.login.hint")}</p>
                <Button asChild className="mt-4 w-full">
                  <a href={`${state.portalUrl}/login`}>
                    <LogIn className="size-4" />
                    {t("publicDoc.login.cta")}
                  </a>
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>
      </main>
    </DocShell>
  );
}
